import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useEffect, useRef, useState } from 'react';
import { usePageSEO } from '@/hooks/usePageSEO';
import MisHireSection from './components/MisHireSection';

const buildFitSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'BuildFit AI',
  url: 'https://buildfitai.net/',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description: 'BuildFit AI is a structured candidate evaluation platform that replaces informal interviews with AI-assisted hiring frameworks. Designed for organizations that take structured hiring seriously.',
  creator: {
    '@type': 'Person',
    name: 'Jeff Dumè',
    url: 'https://jeffdume.com',
  },
  offers: {
    '@type': 'Offer',
    description: 'Contact for pricing. Schedule a demo to see BuildFit AI in action.',
  },
  featureList: [
    'Structured Hiring Framework',
    'Evaluation Analytics & Insights',
    'Role Based Candidate Evaluation',
    'Structured Interview Design',
    'Alignment & Culture Evaluation',
    'Candidate Oversight Hub',
    'Structured Follow Up Prompts',
    'Documented Candidate Reports',
    'Controlled Team Collaboration',
  ],
};

function useScrollReveal(threshold = 0.15) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

export default function BuildFitAI() {
  usePageSEO({
    title: 'BuildFit AI | Structured Candidate Evaluation & AI-Assisted Hiring Platform',
    description: 'BuildFit AI replaces informal interviews with a structured evaluation framework. Improve hiring consistency, reduce bad hires, and strengthen decisions with AI-assisted, human-led candidate evaluation.',
    keywords: 'BuildFit AI, structured hiring platform, AI assisted hiring, candidate evaluation software, structured interviews, hiring framework, AI governance hiring, reduce bad hires, workforce infrastructure technology',
    canonical: '/buildfit-ai',
    ogTitle: 'BuildFit AI | Structured Candidate Evaluation Platform',
    ogDescription: 'Replace informal interviews with structured evaluation frameworks. AI-assisted, human-led hiring decisions that reduce bad hires and improve consistency.',
    schema: buildFitSchema,
    breadcrumbs: [{ name: 'BuildFit AI', item: '/buildfit-ai' }],
  });

  const heroReveal = useScrollReveal();
  const dashboardReveal = useScrollReveal();
  const featuresReveal = useScrollReveal();
  const philosophyReveal = useScrollReveal();
  const ctaReveal = useScrollReveal();

  const candidates = [
    {
      name: 'Sarah Mitchell',
      role: 'VP of Engineering',
      dept: 'Technology',
      status: 'Evaluated',
      statusColor: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
      alignment: 92,
      alignmentColor: 'bg-emerald-500',
      aiMatch: 94,
      lastActive: '2 hrs ago',
    },
    {
      name: 'David Chen',
      role: 'Director of Operations',
      dept: 'Operations',
      status: 'Pending Review',
      statusColor: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
      alignment: 78,
      alignmentColor: 'bg-amber-500',
      aiMatch: 81,
      lastActive: '5 hrs ago',
    },
    {
      name: 'Jennifer Park',
      role: 'Head of Product',
      dept: 'Product',
      status: 'Evaluated',
      statusColor: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
      alignment: 88,
      alignmentColor: 'bg-emerald-500',
      aiMatch: 89,
      lastActive: '1 day ago',
    },
    {
      name: 'Michael Torres',
      role: 'Senior Data Architect',
      dept: 'Technology',
      status: 'Interview Scheduled',
      statusColor: 'bg-sky-500/15 text-sky-400 border-sky-500/20',
      alignment: 71,
      alignmentColor: 'bg-amber-500',
      aiMatch: 74,
      lastActive: '3 days ago',
    },
    {
      name: 'Amanda Foster',
      role: 'Chief of Staff',
      dept: 'Executive',
      status: 'Priority',
      statusColor: 'bg-rose-500/15 text-rose-400 border-rose-500/20',
      alignment: 65,
      alignmentColor: 'bg-rose-500',
      aiMatch: 68,
      lastActive: 'Just now',
    },
  ];

  const features = [
    {
      title: 'Structured Hiring Framework',
      desc: 'Standardized candidate screening and role based evaluation frameworks designed to support consistent and defensible hiring decisions.',
      icon: 'ri-file-list-3-line',
    },
    {
      title: 'Evaluation Analytics & Insights',
      desc: 'Structured dashboards, measurable alignment scoring, and documented insights that translate interview responses into clear hiring intelligence.',
      icon: 'ri-bar-chart-box-line',
    },
    {
      title: 'Role Based Candidate Evaluation',
      desc: 'Evaluate candidates against predefined success criteria and organizational expectations to ensure comparability and documented alignment.',
      icon: 'ri-user-search-line',
    },
    {
      title: 'Structured Interview Design',
      desc: 'Design and configure interview workflows aligned with defined role standards, performance expectations, and evaluation criteria.',
      icon: 'ri-questionnaire-line',
    },
    {
      title: 'Alignment & Culture Evaluation',
      desc: 'Measure alignment with organizational values, leadership expectations, and team standards using structured evaluation criteria.',
      icon: 'ri-heart-pulse-line',
    },
    {
      title: 'Candidate Oversight Hub',
      desc: 'A centralized hub for managing roles, candidates, and evaluation insights so hiring teams can identify and review high interest candidates within a structured hiring environment.',
      icon: 'ri-dashboard-3-line',
    },
    {
      title: 'Structured Follow Up Prompts',
      desc: 'Targeted follow up prompts generated from candidate responses to support deeper evaluation and informed human review.',
      icon: 'ri-chat-follow-up-line',
    },
    {
      title: 'Documented Candidate Reports',
      desc: 'Structured summaries outlining strengths, risks, and alignment insights to support defensible hiring decisions.',
      icon: 'ri-article-line',
    },
    {
      title: 'Controlled Team Collaboration',
      desc: 'Role based access for hiring teams to review candidate records, evaluation summaries, and decision insights across authorized stakeholders.',
      icon: 'ri-team-line',
    },
  ];

  return (
    <div className="bg-charcoal min-h-screen">
      <Navbar />
      <main>
        {/* HERO */}
        <section className="relative pt-20 sm:pt-24 md:pt-32 pb-12 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-charcoal via-[#1a1a1a] to-[#111]"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/90 to-charcoal/60"></div>
          <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div ref={heroReveal.ref}>
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 sm:mb-4">
                Structured Candidate Evaluation
              </p>
              <h1 className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-display font-bold leading-tight mb-4 sm:mb-6">
                BuildFit AI
              </h1>
              <div
                className={`transition-all duration-700 delay-100 ${
                  heroReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
              >
                <p className="text-white/60 text-sm md:text-base max-w-2xl leading-relaxed mb-6 sm:mb-8">
                  BuildFit AI replaces informal interviews with a structured evaluation framework
                  based on defined hiring criteria to improve evaluation consistency, reduce
                  bad hires, and strengthen hiring decisions.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <a
                    href="https://buildfitai.net/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-charcoal text-sm font-semibold px-6 sm:px-8 py-3.5 sm:py-4 rounded-full transition-all duration-300 whitespace-nowrap"
                  >
                    Visit BuildFit AI
                    <i className="ri-external-link-line text-sm"></i>
                  </a>
                  <a
                    href="https://meetings-na2.hubspot.com/mainframesolutions/buildfitaistrategy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-gold text-white text-sm font-semibold px-6 sm:px-8 py-3.5 sm:py-4 rounded-full transition-all duration-300 whitespace-nowrap"
                  >
                    Book a Strategy Call
                    <i className="ri-calendar-line text-sm"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MIS-HIRE PROBLEM + COST ESTIMATOR */}
        <MisHireSection />

        {/* DASHBOARD UI MOCKUP */}
        <section className="py-12 md:py-20 lg:py-24 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div
              ref={dashboardReveal.ref}
              className={`mb-8 sm:mb-10 transition-all duration-700 ${
                dashboardReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3">
                Live Platform
              </p>
              <h2 className="text-white text-xl sm:text-2xl lg:text-3xl font-bold leading-tight">
                The Real BuildFit AI Interface
              </h2>
              <p className="text-white/50 text-sm md:text-base mt-3 max-w-2xl leading-relaxed">
                This is the actual BuildFit AI dashboard showing structured candidate evaluation, hiring frameworks, and human led decision tools built for teams that take hiring seriously.
              </p>
            </div>

            <div
              className={`transition-all duration-700 delay-200 ${
                dashboardReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="bg-dark-card rounded-xl border border-white/5 overflow-hidden">
                <div className="bg-dark-surface px-4 py-3 flex items-center gap-2 border-b border-white/5">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-500/70"></span>
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500/70"></span>
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/70"></span>
                  </div>
                  <span className="text-white/30 text-xs ml-2">buildfitai.net</span>
                </div>
                <div className="p-2 md:p-3">
                  <img
                    src="/images/buildfitscreen.png"
                    alt="BuildFit AI live platform screenshot showing the actual dashboard interface"
                    loading="lazy"
                    className="w-full h-auto rounded-md object-cover object-top"
                  />
                </div>
              </div>

              {/* CTA below the dashboard slide */}
              <div className="mt-6 sm:mt-8 flex justify-center">
                <a
                  href="https://meetings-na2.hubspot.com/mainframesolutions/buildfitaistrategy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-charcoal font-semibold text-sm px-6 sm:px-8 py-3.5 sm:py-4 rounded-full transition-all duration-300 whitespace-nowrap"
                >
                  Reduce Your Hiring Risk
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURES GRID — 9 MODULES */}
        <section className="py-12 md:py-20 lg:py-24 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div
              ref={featuresReveal.ref}
              className={`mb-8 sm:mb-12 transition-all duration-700 ${
                featuresReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3">
                Platform Modules
              </p>
              <h2 className="text-white text-xl sm:text-2xl lg:text-3xl font-bold leading-tight">
                Everything You Need for Structured Hiring
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {features.map((feature, i) => (
                <div
                  key={feature.title}
                  className={`bg-dark-card rounded-xl p-5 sm:p-6 md:p-8 border border-white/5 hover:border-gold/20 transition-all duration-500 ${
                    featuresReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${200 + i * 80}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-lg mb-4 sm:mb-5">
                    <i className={`${feature.icon} text-gold text-lg`}></i>
                  </div>
                  <h3 className="text-white font-semibold text-sm sm:text-base mb-2">{feature.title}</h3>
                  <p className="text-white/40 text-xs sm:text-sm leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PHILOSOPHY */}
        <section className="py-12 md:py-20 lg:py-24 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
            <div
              ref={philosophyReveal.ref}
              className={`bg-gold/5 border border-gold/10 rounded-xl p-5 sm:p-6 md:p-8 transition-all duration-700 ${
                philosophyReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-gold/10 rounded-lg shrink-0">
                  <i className="ri-lightbulb-line text-gold"></i>
                </div>
                <h3 className="text-white font-semibold text-base sm:text-lg">The BuildFit AI Philosophy</h3>
              </div>
              <p className="text-white/50 text-sm md:text-base leading-relaxed">
                BuildFit AI is not an automated hiring tool. It is an infrastructure platform that
                makes human led hiring more consistent, more fair, and more effective. Every
                evaluation framework is defined by the people who understand the role. Every hiring
                decision remains in human hands. AI organizes, structures, and supports. It does not
                replace judgment.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 md:py-16 lg:py-20 border-t border-white/5">
          <div
            ref={ctaReveal.ref}
            className={`w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-3xl mx-auto text-center transition-all duration-700 ${
              ctaReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-white text-xl sm:text-2xl font-bold mb-3 sm:mb-4">
              See BuildFit AI in Action
            </h2>
            <p className="text-white/50 text-sm md:text-base mb-6 sm:mb-8">
              Schedule a demo to see how structured evaluation frameworks can reduce hiring risk and
              improve decision quality at your organization.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <a
                href="https://buildfitai.net/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-charcoal font-semibold text-sm px-6 sm:px-8 py-3.5 sm:py-4 rounded-full transition-all duration-300 whitespace-nowrap"
              >
                Visit BuildFit AI
                <i className="ri-external-link-line"></i>
              </a>
              <a
                href="https://meetings-na2.hubspot.com/mainframesolutions/buildfitaistrategy"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-white/20 hover:border-gold text-white font-semibold text-sm px-6 sm:px-8 py-3.5 sm:py-4 rounded-full transition-all duration-300 whitespace-nowrap"
              >
                Book a Strategy Call
                <i className="ri-calendar-line"></i>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}